// frontend/src/services/backgroundPhotoService.js
import api from './api';

class BackgroundPhotoService {
    constructor() {
        this.stream = null;
        this.isCapturing = false;
        this.photoQueue = [];
        this.isProcessing = false;
        this.cameraAvailable = false;
    }

    // ============================================
    // CHECK CAMERA AVAILABILITY
    // ============================================
    async checkCameraAvailability() {
        try {
            console.log('📸 Checking camera availability...');
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 320 },
                    height: { ideal: 240 }
                },
                audio: false
            });
            stream.getTracks().forEach(track => track.stop());
            this.cameraAvailable = true;
            console.log('✅ Camera is available');
            return true;
        } catch (error) {
            console.warn('⚠️ Camera not available:', error.message);
            this.cameraAvailable = false;
            return false;
        }
    }

    // ============================================
    // CAPTURE PHOTO IN BACKGROUND
    // ============================================
    async captureBackgroundPhoto(accessToken, photoOrder) {
        console.log(`📸 Capturing background photo ${photoOrder}`);

        // Check if camera is available
        if (!this.cameraAvailable) {
            const available = await this.checkCameraAvailability();
            if (!available) {
                console.error('❌ Camera not available for capture');
                return { success: false, error: 'Camera not available' };
            }
        }

        let video = null;
        let stream = null;
        let canvas = null;

        try {
            // Get camera stream
            stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 320 },
                    height: { ideal: 240 },
                },
                audio: false
            });

            // Create hidden video element
            video = document.createElement('video');
            video.srcObject = stream;
            video.setAttribute('playsinline', '');
            video.setAttribute('muted', '');
            video.style.display = 'none';
            video.style.position = 'fixed';
            video.style.top = '-9999px';
            video.style.left = '-9999px';
            document.body.appendChild(video);

            // Wait for video to be ready
            await new Promise((resolve) => {
                video.onloadedmetadata = () => {
                    video.play();
                    resolve();
                };
                // Fallback: resolve after 2 seconds if onloadedmetadata doesn't fire
                setTimeout(resolve, 2000);
            });

            // Wait a moment for the camera to stabilize
            await new Promise(resolve => setTimeout(resolve, 500));

            // Capture frame
            canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 320;
            canvas.height = video.videoHeight || 240;
            const context = canvas.getContext('2d');
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Get image data
            const imageData = canvas.toDataURL('image/jpeg', 0.8);

            console.log(`📸 Image captured, size: ${imageData.length} bytes`);

            // Submit photo to server using the API service
            const response = await api.post(`/face-photos/${accessToken}/capture`, {
                photo_order: photoOrder,
                image: imageData,
                background: true
            });

            if (response.data.success) {
                console.log(`✅ Background photo ${photoOrder} captured successfully`);
                return { success: true, data: response.data };
            } else {
                console.error(`❌ Server returned error:`, response.data.error);
                return { success: false, error: response.data.error };
            }

        } catch (error) {
            console.error('❌ Background photo capture failed:', error);
            return { success: false, error: error.message };
        } finally {
            // Clean up
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            if (video && video.parentNode) {
                video.remove();
            }
            if (canvas && canvas.parentNode) {
                canvas.remove();
            }
        }
    }

    // ============================================
    // CAPTURE PHOTO 1 (Before Assessment)
    // ============================================
    async capturePhoto1(accessToken) {
        return this.captureBackgroundPhoto(accessToken, 1);
    }

    // ============================================
    // CAPTURE PHOTO 2 (Middle of Assessment)
    // ============================================
    async capturePhoto2(accessToken) {
        return this.captureBackgroundPhoto(accessToken, 2);
    }

    // ============================================
    // CAPTURE PHOTO 3 (End of Assessment)
    // ============================================
    async capturePhoto3(accessToken) {
        return this.captureBackgroundPhoto(accessToken, 3);
    }
}

export default new BackgroundPhotoService();